import { Modal } from '@/ui';
import PropTypes from 'prop-types';
import './terms-and-conditions.css';

// Fallback only, for when the catalog read fails (see useTermsCatalog.js).
// The success path renders the published PDF the catalog points to; this
// hardcoded copy is not the source of truth, it's what the user sees if the
// API is unreachable at flow init.
export const FALLBACK_TYC_VERSION = '1.0.0';

const FALLBACK_SECTIONS = [
  {
    title: '1. Identificación del proveedor y aceptación',
    paragraphs: [
      'Los presentes Términos y Condiciones de Uso (en adelante, los "Términos") regulan el acceso y uso de la plataforma tecnológica Recepthor (en adelante, la "Plataforma"), de titularidad de Tesseron Technology SpA, RUT N° 78.098.391-4, con domicilio en Eulogia Sánchez 065, Providencia, Chile (en adelante, "Recepthor", "nosotros" o la "Empresa").',
      'La Plataforma está dirigida exclusivamente a abogados habilitados para el ejercicio de la profesión, estudios jurídicos u otros profesionales del área legal que actúen en representación de una de las partes en causas judiciales (en adelante, el "Usuario" o los "Usuarios").',
      'Al registrarse, acceder o utilizar la Plataforma, el Usuario declara haber leído y comprendido estos Términos y los acepta íntegramente. Si el Usuario no está de acuerdo con estos Términos, no debe usar la Plataforma.',
      'Estos Términos constituyen un contrato entre el Usuario y Tesseron Technology SpA, y se complementan con la Política de Privacidad de la Plataforma (la "Política de Privacidad").',
    ],
  },
  {
    title: '2. Objeto de la Plataforma',
    paragraphs: [
      'La Plataforma Recepthor tiene por finalidad brindar a los Usuarios herramientas tecnológicas para consultar, visualizar, organizar y gestionar información relativa a causas judiciales a partir de información obtenida desde fuentes oficiales de acceso público, principalmente el portal de Internet del Poder Judicial de Chile (www.pjud.cl) y su Consulta Unificada de Causas, así como la Oficina Judicial Virtual y otros sistemas electrónicos que correspondan.',
      'Recepthor no es parte del Poder Judicial, no interviene en la tramitación de causas, no presta servicios de patrocinio ni representación judicial, ni reemplaza el criterio profesional del abogado. La información mostrada por la Plataforma tiene carácter referencial y en ningún caso reemplaza la revisión directa de la carpeta electrónica en los sistemas oficiales del Poder Judicial.',
      'El uso de la Plataforma no crea relación abogado–cliente entre Recepthor y los clientes del Usuario, ni constituye asesoría legal por parte de Recepthor al Usuario o a terceros.',
    ],
  },
  {
    title: '3. Relación con el Poder Judicial y uso de información judicial',
    paragraphs: [
      'La información que se presenta en la Plataforma proviene, principalmente, de la Consulta Unificada de Causas del Poder Judicial y otros sistemas oficiales de dicho órgano, en la medida en que sean accesibles al público o al Usuario según su perfil y legitimación. Recepthor no modifica el contenido de dicha información, sino que la reproduce y ordena para facilitar su consulta por parte del Usuario.',
      'El Poder Judicial, a través de sus Condiciones de Uso de la Consulta Unificada de Causas, establece obligaciones y restricciones, entre otras: 1. El uso diligente, correcto y lícito de la información; 2. La exclusión de causas, sujetos o trámites declarados reservados o secretos; 3. La prohibición de uso y/o reproducción de la información con propósitos comerciales o para fines que atenten contra los legítimos derechos de terceros, especialmente en causas reservadas. El Usuario declara conocer y respetar dichas condiciones, así como las leyes aplicables y las instrucciones que emita el Poder Judicial en el futuro.',
      'Recepthor no tiene control sobre las condiciones de uso de los portales del Poder Judicial ni sobre eventuales modificaciones, restricciones de acceso, caídas de servicio u otros cambios. El Usuario acepta que el acceso y uso de la información judicial se realiza bajo su exclusiva responsabilidad, debiendo utilizar la Plataforma únicamente en la medida en que ello sea compatible con la normativa vigente y con los términos de uso de los sistemas del Poder Judicial.',
      'Recepthor no es responsable frente al Usuario ni frente a terceros por: 1. Todo cambio o limitación que el Poder Judicial imponga al acceso, uso o reproducción de la información; 2. La suspensión o modificación de sus sistemas electrónicos; 3. Eventuales reclamos o acciones de terceros o de organismos públicos vinculados con la forma en que el Usuario utiliza la información obtenida a través de la Plataforma.',
    ],
  },
  {
    title: '4. Condiciones especiales para abogados y estudios jurídicos',
    paragraphs: [
      'Al crear una cuenta y utilizar la Plataforma, el Usuario declara y garantiza que: a) Es abogado habilitado para el ejercicio de la profesión en Chile o actúa por cuenta y representación de un estudio jurídico o institución que cuenta con abogados habilitados. b) La información de causas que ingresa en la Plataforma corresponde a causas en que interviene como parte, mandatario judicial o abogado patrocinante de una de las partes. c) Posee mandato y/o autorización suficiente y vigente para actuar en representación de la parte respectiva y para utilizar la información de la causa en la Plataforma. d) Ha informado a sus clientes, cuando corresponda, sobre el uso de herramientas tecnológicas de terceros como Recepthor, en conformidad con las normas de ética profesional y de protección de datos personales.',
      'El Usuario se obliga a ingresar en la Plataforma únicamente causas que efectivamente administra o patrocina y respecto de las cuales tiene legitimación para acceder a su información.',
      'El Usuario se compromete a mantener indemne y a indemnizar a Tesseron Technology SpA, sus socios, directores y trabajadores por todo daño, perjuicio, multa, sanción o gasto (incluidos honorarios de abogados) que derive del uso indebido de la Plataforma.',
    ],
  },
  {
    title: '5. Funcionamiento de la Plataforma y acceso automatizado',
    paragraphs: [
      'La Plataforma puede utilizar herramientas técnicas, incluyendo mecanismos automatizados de acceso y consulta, para obtener información desde sistemas públicos o accesibles al Usuario, siempre dentro de las capacidades técnicas y restricciones de dichos sistemas.',
      'Recepthor no ofrece ni fomenta el uso de la Plataforma para realizar búsquedas masivas, sistemáticas o indiscriminadas de información judicial. El Usuario se obliga a utilizar la Plataforma únicamente para la gestión de las causas concretas que administra.',
    ],
  },
  {
    title: '6. Uso permitido de la Plataforma',
    paragraphs: [
      'El Usuario se obliga a utilizar la Plataforma y sus contenidos de forma diligente, correcta y lícita, evitando todo uso que pueda considerarse contrario a la ley, al orden público, a la ética profesional o a derechos de terceros.',
      'Recepthor podrá suspender o cancelar la cuenta del Usuario cuando detecte o tenga indicios razonables de uso contrario a estos Términos o incumplimiento de la normativa vigente.',
    ],
  },
  {
    title: '7. Propiedad intelectual e industrial',
    paragraphs: [
      'Todos los derechos de propiedad intelectual e industrial sobre la Plataforma corresponden exclusivamente a Tesseron Technology SpA o a sus licenciantes.',
      'El Usuario recibe una licencia limitada, no exclusiva, intransferible y revocable para utilizar la Plataforma, exclusivamente para fines profesionales lícitos.',
    ],
  },
  {
    title: '8. Datos personales y confidencialidad',
    paragraphs: [
      'Tesseron Technology SpA realizará el tratamiento de datos personales conforme a la normativa chilena vigente y a la Política de Privacidad de la Plataforma.',
      'Tesseron Technology SpA tratará como confidencial toda información de las causas y de los clientes del Usuario, salvo que dicha información sea de acceso público, exista obligación legal de divulgación, o el Usuario autorice expresamente su comunicación.',
    ],
  },
  {
    title: '9. Limitación de responsabilidad',
    paragraphs: [
      'La Plataforma se ofrece "tal cual" y "según disponibilidad". Tesseron Technology SpA no garantiza que la Plataforma esté libre de errores ni que funcione sin interrupciones.',
      'En la medida máxima permitida por la ley, la responsabilidad total de Tesseron Technology SpA se limitará al monto efectivamente pagado por el Usuario durante los tres (3) meses anteriores al hecho que dé lugar a la responsabilidad.',
    ],
  },
  {
    title: '10. Modificaciones de la Plataforma y los Términos',
    paragraphs: [
      'Tesseron Technology SpA podrá modificar estos Términos en cualquier momento. Las modificaciones entrarán en vigencia una vez publicadas en la Plataforma. El uso continuado implicará su aceptación.',
    ],
  },
  {
    title: '11. Vigencia, terminación y suspensión',
    paragraphs: [
      'Estos Términos se aplican desde el momento en que el Usuario crea su cuenta y se mantendrán vigentes mientras la Plataforma se encuentre operativa o hasta que el Usuario solicite la eliminación de su cuenta.',
    ],
  },
  {
    title: '12. Ley aplicable y jurisdicción',
    paragraphs: [
      'Estos Términos se regirán conforme a las leyes de la República de Chile. Las partes fijan su domicilio en Santiago y se someten a la jurisdicción de sus tribunales ordinarios.',
    ],
  },
  {
    title: '13. Contacto',
    paragraphs: [
      'Empresa: Tesseron Technology SpA\nCorreo electrónico: contacto@recepthor.cl\nDomicilio: Eulogia Sanchez 065, Providencia\nSitio web: www.recepthor.cl',
    ],
  },
];

export default function TermsAndConditions({
  catalog = null,
  loading = false,
  show,
  onClose,
}) {
  return (
    <Modal open={show} onClose={onClose} size="large">
      <Modal.Header title="Términos y Condiciones" onClose={onClose} />
      <Modal.Body>
        <div className="terms-modal-content">
          {/* While the catalog read is in flight, the fallback copy would show
              a version a late success replaces. */}
          {loading ? (
            <p className="terms-catalog-copy">
              Cargando los términos vigentes…
            </p>
          ) : catalog?.version && catalog?.pdfUrl ? (
            <div className="terms-catalog">
              <p className="terms-version">Versión {catalog.version}</p>
              <p className="terms-catalog-copy">{catalog.title}</p>
              <a
                className="terms-catalog-link"
                href={catalog.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Ver términos y condiciones (PDF)
              </a>
            </div>
          ) : (
            <>
              <p className="terms-version">Versión {FALLBACK_TYC_VERSION}</p>
              {FALLBACK_SECTIONS.map((section) => (
                <div key={section.title} className="terms-section">
                  <h3 className="terms-section-title">{section.title}</h3>
                  {section.paragraphs.map((text, i) => (
                    <p key={i} className="terms-section-paragraph">
                      {text}
                    </p>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

TermsAndConditions.propTypes = {
  // The catalog entry read at flow init (see useTermsCatalog.js): { version,
  // title, pdfUrl, effectiveDate }. null renders the fallback copy once the
  // read has settled without a usable catalog.
  catalog: PropTypes.shape({
    pdfUrl: PropTypes.string,
    title: PropTypes.string,
    version: PropTypes.string,
  }),
  // True while the catalog read is in flight: renders a loading line instead
  // of either version.
  loading: PropTypes.bool,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
